<template>
	<App title="Update Credit Plan">
		<div class="container py-4">
			<!-- Page Header -->
			<div class="row mb-4">
				<div class="col-12">
					<div class="d-flex align-items-center gap-0 mb-3">
						<div>
							<h2 class="fw-bold mb-0">Update Credit Plan</h2>
							<p class="text-muted mb-0">Modify credit plan details for {{ credit.rider.firstname }} {{ credit.rider.lastname }}</p>
						</div>
					</div>
				</div>
			</div>

			<div class="row gap-0">
				<!-- Left Column - Rider Summary -->
				<div class="col-lg-4 mb-4">
					<div class="card border-0  sticky-top bg-light" style="top: 1rem;">
						<div class="card-header bg-body-tertiary text-dark-50 py-3 m-1">
							<h5 class="mb-0">
								<i class="fas fa-user-circle me-2"></i>Rider Information
							</h5>
						</div>
						<div class="card-body">
							<!-- Rider Avatar -->
							<div class="text-center mb-3">
								<div class="rounded-circle overflow-hidden border border-3 border-light shadow-sm mx-auto" 
									style="width: 100px; height: 100px;">
									<img 
										:src="credit.rider?.avatar?.url || '/default-avatar.png'" 
										alt="Avatar" 
										class="img-fluid object-fit-cover h-100 w-100" 
									/>
								</div>
							</div>

							<!-- Rider Details -->
							<div class="mb-3">
								<h6 class="fw-bold text-center mb-3">{{ credit.rider.firstname }} {{ credit.rider.lastname }}</h6>
								
								<div class="small mb-2">
									<i class="fas fa-envelope text-muted me-2"></i>
									<span>{{ credit.rider.email }}</span>
								</div>
								
								<div class="small mb-2">
									<i class="fas fa-phone text-muted me-2"></i>
									<span>{{ credit.rider.phone }}</span>
								</div>
								
								<div class="small mb-2">
									<i class="fas fa-map-marker-alt text-muted me-2"></i>
									<span>{{ credit.rider.address?.state }}, {{ credit.rider.address?.country }}</span>
								</div>
							</div>

							<hr>

							<!-- Current Credit Summary -->
							<div class="mb-3">
								<h6 class="fw-semibold mb-3">
									<i class="fas fa-credit-card me-2"></i>Current Credit Status
								</h6>
								
								<div class="d-flex justify-content-between mb-2">
									<span class="text-muted small">Credit Type:</span>
									<span class="badge bg-dark">{{ credit.credit_type }}</span>
								</div>
								
								<div class="d-flex justify-content-between mb-2">
									<span class="text-muted small">Total Amount:</span>
									<span class="fw-semibold">{{ credit.totalAmount?.formatted }}</span>
								</div>
								
								<div class="d-flex justify-content-between mb-2">
									<span class="text-muted small">Per Swap:</span>
									<span class="fw-semibold">{{ credit.repaymentPerSwap?.formatted }}</span>
								</div>
								
								<div class="d-flex justify-content-between mb-2">
									<span class="text-muted small">Paid:</span>
									<span class="fw-semibold">{{ credit.paidSequence }} / {{ credit.numberOfRepayments }}</span>
								</div>
								
								<div class="d-flex justify-content-between mb-3">
									<span class="text-muted small">Balance:</span>
									<span class="fw-semibold text-danger">{{ credit.balance?.formatted }}</span>
								</div>

								<!-- Progress Bar -->
								<div class="mb-2">
									<div class="d-flex justify-content-between small mb-1">
										<span class="text-muted">Progress</span>
										<span class="fw-semibold">{{ progressPercentage }}%</span>
									</div>
									<div class="progress" style="height: 8px;">
										<div 
											class="progress-bar bg-success" 
											role="progressbar" 
											:style="{width: progressPercentage + '%'}"
										></div>
									</div>
								</div>
							</div>

						
						</div>
					</div>
				</div>

				<!-- Right Column - Update Form -->
				<div class="col-lg-8">
					<div class="card border-0 shadow-sm">
						<div class="card-header bg-white border-0 py-3">
							<h5 class="mb-0">
								Update Credit Details
							</h5>
						</div>
						<div class="card-body p-4" v-if="credit.paidSequence === 0">
							<!-- Update Form Component -->
							<CreditUpdateForm
								:url="route('creditrepair.ajax.update', { creditrepair: credit.id })"
								:credit-data="credit"
								:rider-id="credit.rider.id"
								:can-select ="true"
                                :credit-types ="types"
								:existing-agreement="credit.credit_agreement"
								@success="handleUpdateSuccess"
								@error="handleUpdateError"
								@cancel="handleCancel"
							/>
						</div>
						<div class="card-body p-4" v-else>
							<h6 class="fw-semibold text-dark"> 
								This credit process has already started or been completed. 
								Please review the details before proceeding or contact support!
							</h6>

						</div>
					</div>

					
				</div>
			</div>
		</div>

		<!-- Notifications -->
		<NotivueKeyboard v-slot="{ containersTabIndex }">
			<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
				<AppNotification :item="item" />
			</Notivue>
		</NotivueKeyboard>
	</App>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'
import App from '@/vue/templates/App.vue'
import CreditUpdateForm from '@/vue/components/CreditUpdateForm.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import { Link } from '@inertiajs/vue3'
import { materialTheme, Notification, NotificationProgress, Notivue, NotivueKeyboard } from 'notivue'
import Swal from 'sweetalert2'

interface Credit {
	id: string | number
	creditType?: string
	totalAmount?: {
		amount: string
		currency: string
		formatted: string
	}
	repaymentPerSwap?: {
		amount: string
		currency: string
		formatted: string
	}
	numberOfRepayments: number
	paidSequence: number
	skippedSequence?: number
	balance?: {
		amount: string
		currency: string
		formatted: string
	}
	activatedAt?: string
	completedAt?: string
}

interface Rider {
	id: string | number
	firstname: string
	lastname: string
	email: string
	phone: string
	address?: {
		street?: string
		state?: string
		country?: string
	}
	avatar?: {
		url?: string
	}
}

interface Activity {
	id: string | number
	description: string
	date: string
	type: 'payment' | 'update' | 'skip'
}

@Component({
	components: {
		App,
		CreditUpdateForm,
		AppNotification,
		Link,
		Notivue,
		NotivueKeyboard,
		Notification,
		NotificationProgress
	}
})
class CreditUpdatePage extends Vue {
	materialTheme = materialTheme
	recentActivity: Activity[] = []



	@Prop({ type: Object, required: true })
	readonly credit!: Credit

	@Prop({ type: Array})
	readonly types!: any

	get creditTypeLabel(): string {
		const typeMap: Record<string, string> = {
			'regular': 'Regular Credit',
			'internal_repair': 'Credit Repair',
			'external_repair': 'External Repair'
		}
		return typeMap[this.credit.creditType || ''] || 'Unknown'
	}

	get progressPercentage(): number {
		if (!this.credit.numberOfRepayments) return 0
		return Math.round((this.credit.paidSequence / this.credit.numberOfRepayments) * 100)
	}

	mounted() {
		this.recentActivity = this.activity
	}

	async handleUpdateSuccess({ message, data }) {
		Swal.fire({
			title: 'Success!',
			text: message,
			icon: 'success',
			confirmButtonText: 'OK',
			timer: 3000
		}).then(() => {
			// Redirect back to rider profile
			this.router.visit(this.route('rider.page.single',{rider: this.credit?.rider.id}))
		})
	}

	handleUpdateError({ message, error }) {
		console.error('Update error:', error)
		
		Swal.fire({
			title: 'Error!',
			text: message,
			icon: 'error',
			confirmButtonText: 'OK'
		})
	}

	handleCancel() {
		// Navigate back to rider profile
		this.router.visit(this.route('rider.page.single', this.rider.id))
	}
}

export default toNative(CreditUpdatePage)
</script>

<style scoped>

.sticky-top {
	position: sticky;
	z-index: 1020;
}



.list-group-item {
	transition: background-color 0.2s ease;
}

.list-group-item:hover {
	background-color: #f8f9fa;
}

.progress {
	background-color: #e9ecef;
	border-radius: 0.5rem;
}

.progress-bar {
	transition: width 0.6s ease;
}

@media (max-width: 992px) {
	.sticky-top {
		position: relative;
		top: 0 !important;
	}
}
</style>